import {PreferencesPage} from "@/pages/dashboard/admin/preference-page.tsx";
import {MentorshipGroupsPage} from "@/pages/dashboard/admin/mentorship-groups-page.tsx";
import {Card, CardContent, CardDescription, CardHeader} from "@/components/ui/card.tsx";
import {PageTitle, SectionTitle} from "@/components/ui/headings";
import {useQuery} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getSummary} from "@/services/suggestionService.ts";
import {getAuditLog} from "@/services/auditLogService.ts";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {AdminStatCards, AdminGapSection, AdminTrendSection} from "@/pages/dashboard/admin/admin-insights-page.tsx";
import {MentorshipCalendar} from "@/components/MentorshipCalendar.tsx";
import {useTranslation} from "react-i18next";
import {isPreferenceSubmissionPeriod, isBeforeMentorSelectionDeadline} from "@/utils/time-helper.ts";
import {useEffect, useMemo, useState} from "react";
import {getSeasons} from "@/services/seasonService.ts";
import {getMentors, getMentorProfileCompletionStats} from "@/services/mentorService.ts";
import {getUnmatchedDiagnosis, getMenteePreferenceStats, getMentorSelectionStats} from "@/services/menteeService.ts";
import {Season} from "@/types/Season.ts";
import {Pie, PieChart, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid} from "recharts";
import {ChartContainer, ChartTooltip, ChartTooltipContent} from "@/components/ui/chart.tsx";

type AdminDashboardProps = {
    submitPreferenceDeadline: Date | undefined;
    selectMentorDeadline: Date | undefined;
};

type Phase = "preference-submission" | "mentor-selection" | "after-selection";

type SimpleChartData = {
    timeSeries: { day: string; total: number }[];
};

type InlineLineChartProps = {
    data: SimpleChartData;
    labelKey: string;
};

const InlineLineChart = ({data, labelKey}: InlineLineChartProps) => {
    const {t} = useTranslation();

    return (
        <Card>
            <CardHeader>
                <CardDescription className="text-sm font-medium text-text-2">
                    {t(labelKey)}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <ChartContainer
                    className="mt-2 h-[160px] w-full"
                    config={{
                        total: {label: t(labelKey), color: "rgb(var(--accent) / 1)"},
                    }}
                >
                    <LineChart
                        data={data.timeSeries}
                        margin={{top: 4, right: 4, left: 0, bottom: 0}}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-border"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="day"
                            tickLine={false}
                            axisLine={false}
                            tick={{fontSize: 11, fill: "rgb(var(--text-3) / 1)"}}
                        />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tick={{fontSize: 11, fill: "rgb(var(--text-3) / 1)"}}
                            width={28}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line
                            type="monotone"
                            dataKey="total"
                            stroke="var(--color-total)"
                            strokeWidth={2}
                            dot={false}
                        />
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
};

function computePhase(submitPreferenceDeadline: Date | undefined, selectMentorDeadline: Date | undefined): Phase {
    if (isPreferenceSubmissionPeriod(submitPreferenceDeadline)) return "preference-submission";
    if (isBeforeMentorSelectionDeadline(selectMentorDeadline)) return "mentor-selection";
    return "after-selection";
}

const AdminDashboardPage = ({submitPreferenceDeadline, selectMentorDeadline}: AdminDashboardProps) => {
    const {t} = useTranslation();
    const [activeSeasonId, setActiveSeasonId] = useState<number | null>(null);

    const phase = useMemo(
        () => computePhase(submitPreferenceDeadline, selectMentorDeadline),
        [submitPreferenceDeadline, selectMentorDeadline],
    );

    const {data: seasons} = useQuery({
        queryKey: queryKeys.seasons.all,
        queryFn: () => getSeasons(),
        initialData: [],
    });

    useEffect(() => {
        if (activeSeasonId != null || seasons.length === 0) return;
        const activeSeason = seasons.find((s: Season) => s.isActive) ?? seasons[0];
        setActiveSeasonId(activeSeason?.id ?? null);
    }, [seasons, activeSeasonId]);

    const seasonId = activeSeasonId ?? undefined;
    const activeSeason = useMemo(
        () => seasons.find((s: Season) => s.id === activeSeasonId) ?? seasons.find((s: Season) => s.isActive) ?? seasons[0],
        [seasons, activeSeasonId],
    );

    const phaseTitle = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (submitPreferenceDeadline) {
            const pref = new Date(submitPreferenceDeadline);
            pref.setHours(23, 59, 59, 999);
            if (today < pref) return t("dashboard.phaseProfileCompletion");
        }
        if (selectMentorDeadline) {
            const sel = new Date(selectMentorDeadline);
            sel.setHours(23, 59, 59, 999);
            if (today <= sel) return t("dashboard.phaseMentorSelection");
        }
        return activeSeason?.name ?? t("dashboard.phaseMentorshipSeason");
    }, [submitPreferenceDeadline, selectMentorDeadline, activeSeason?.name, t]);

    const {data: summary, isFetching: fetchingSummary} = useQuery({
        queryKey: [...queryKeys.fetchSummary.gaps, seasonId ?? "active"],
        queryFn: () => getSummary(seasonId),
        enabled: activeSeasonId != null,
    });

    const {data: unmatchedDiagnosis} = useQuery({
        queryKey: [...queryKeys.fetchMentees.all, "unmatched", seasonId ?? "active"],
        queryFn: () => getUnmatchedDiagnosis(seasonId),
        enabled: activeSeasonId != null,
    });

    const unmatchedBreakdown = useMemo(() => {
        if (!unmatchedDiagnosis) return [];
        return [
            { key: "no_preference", label: t("dashboard.unmatchedNoPreference"), value: unmatchedDiagnosis.reasons.no_preference, color: "#FF792E" },
            { key: "no_suggestions", label: t("dashboard.unmatchedNoSuggestions"), value: unmatchedDiagnosis.reasons.no_suggestions, color: "#FF792E" },
            { key: "not_selected", label: t("dashboard.unmatchedNotSelected"), value: unmatchedDiagnosis.reasons.not_selected, color: "#2BE081" },
        ];
    }, [t, unmatchedDiagnosis]);

    const {data: auditLog} = useQuery({
        queryKey: queryKeys.auditLog({ limit: 8 }),
        queryFn: () => getAuditLog({ limit: 8 }),
    });

    const {data: mentorsForCalendar = []} = useQuery({
        queryKey: [...queryKeys.fetchMentors.all, seasonId ?? "active", "calendar"],
        queryFn: () => getMentors("?include=mentorship-group", seasonId),
        enabled: activeSeasonId != null,
        initialData: [],
    });

    // Profile completion stats chart
    const {data: completionStats} = useQuery({
        queryKey: queryKeys.fetchMentors.profileCompletionStats,
        queryFn: () => getMentorProfileCompletionStats(),
    });

    // Preference submission stats chart
    const {data: preferenceStats} = useQuery({
        queryKey: queryKeys.fetchMentees.preferenceStats,
        queryFn: () => getMenteePreferenceStats(),
    });

    // Mentor selection stats chart (Phase 2+)
    const {data: selectionStats} = useQuery({
        queryKey: [...queryKeys.fetchMentees.all, "mentor-selection-stats"],
        queryFn: () => getMentorSelectionStats(),
        enabled: phase !== "preference-submission",
    });

    if (fetchingSummary) {
        return (
            <div className="flex flex-col space-y-3 ml-6">
                <div className="space-y-2">
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                </div>
            </div>
        );
    }

    /* ---------- Shared sub-sections ---------- */

    const statCardsBlock = summary && summary.preferenceCount > 0 && (
        <AdminStatCards summary={summary} />
    );

    const gapBlock = summary && summary.preferenceCount > 0 && (
        <AdminGapSection summary={summary} seasonId={seasonId ?? activeSeason?.id ?? undefined} />
    );

    const trendBlock = <AdminTrendSection />;

    const preferencesBlock = (
        <PreferencesPage submitPreferenceDeadline={submitPreferenceDeadline} seasonId={seasonId}/>
    );

    const mentorshipGroupsBlock = (
        <>
            <SectionTitle className="p-6">{t("dashboard.mentorshipGroups")}</SectionTitle>
            {phase === "preference-submission" ? (
                <Card className="ml-6">
                    <CardHeader>
                        <CardDescription className="text-base">
                            {t("dashboard.mentorshipGroupsWillBeAvailable")}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : <MentorshipGroupsPage seasonId={seasonId}/>}
        </>
    );

    const calendarBlock = mentorsForCalendar.length > 0 && (
        <div className="p-6 pt-0">
            <MentorshipCalendar mentors={mentorsForCalendar} />
        </div>
    );

    const unmatchedDiagnosisCard = (
        <Card>
            <CardHeader>
                <CardDescription className="text-base">
                    {t("dashboard.unmatchedTitle")}
                </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6">
                {unmatchedDiagnosis?.totalUnmatched ? (
                    <>
                        <div className="flex flex-wrap gap-3 text-xs pb-4">
                            <span className="inline-flex items-center gap-2 rounded-full bg-ash px-3 py-1 text-slate">
                                {t("dashboard.unmatchedTotal")}: {unmatchedDiagnosis.totalUnmatched}
                            </span>
                        </div>
                        <div className="grid gap-6 md:grid-cols-[240px,1fr] md:items-center">
                            <ChartContainer className="h-[220px]">
                                <PieChart>
                                    <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                                    <Pie
                                        data={unmatchedBreakdown.filter((item) => item.value > 0)}
                                        dataKey="value"
                                        nameKey="label"
                                        innerRadius={55}
                                        outerRadius={85}
                                        strokeWidth={1}
                                    >
                                        {unmatchedBreakdown.filter((item) => item.value > 0).map((item) => (
                                            <Cell key={item.key} fill={item.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ChartContainer>
                            <div className="space-y-2 text-sm">
                                {unmatchedBreakdown.map((item) => (
                                    <div key={item.key} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                                            <span className="text-muted-foreground">{item.label}</span>
                                        </div>
                                        <span className="font-medium">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        {t("dashboard.unmatchedEmpty")}
                    </div>
                )}
            </div>
        </Card>
    );

    const activityTimelineCard = (
        <Card>
            <CardHeader>
                <CardDescription className="text-base">
                    {t("dashboard.activityTimelineTitle")}
                </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6">
                {auditLog?.entries?.length ? (
                    <ul className="space-y-3 text-sm">
                        {(auditLog.entries.slice(0, 3)).map((entry) => (
                            <li key={entry.id} className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 rounded-full bg-springGreen shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium">
                                        {entry.userFullName} · {entry.action}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {entry.entityType ? `${entry.entityType}${entry.entityId ? ` #${entry.entityId}` : ""}` : t("dashboard.activityTimelineSystem")}
                                        {" · "}
                                        {new Date(entry.createdAt).toLocaleString()}
                                    </div>
                                    {entry.details && (
                                        <div className="text-xs text-muted-foreground">{entry.details}</div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        {t("dashboard.activityTimelineEmpty")}
                    </div>
                )}
                <div className="pt-3 text-sm">
                    <a href="/audit-log" className="text-primary hover:underline">
                        {t("dashboard.activityTimelineViewLog")}
                    </a>
                </div>
            </div>
        </Card>
    );

    const profileCompletionChartData: SimpleChartData | null = completionStats ? {
        timeSeries: completionStats.timeSeries,
    } : null;

    const preferenceSubmissionChartData: SimpleChartData | null = preferenceStats ? {
        timeSeries: preferenceStats.timeSeries,
    } : null;

    const selectionChartData: SimpleChartData | null = selectionStats ? {
        timeSeries: selectionStats.timeSeries,
    } : null;

    return (
        <>
            <div className="container pt-12">
                <div className="pb-4">
                    <PageTitle>{phaseTitle}</PageTitle>
                </div>

                {/* ---------- PHASE 1: Preference Submission Period ---------- */}
                {phase === "preference-submission" && (
                    <>
                        {statCardsBlock}

                        {/* Two line charts side-by-side */}
                        <div className="p-6 pt-0 grid gap-4 md:grid-cols-2">
                            {profileCompletionChartData && (
                                <InlineLineChart
                                    data={profileCompletionChartData}
                                    labelKey="dashboard.profileCompletionsOverTime"
                                />
                            )}
                            {preferenceSubmissionChartData && (
                                <InlineLineChart
                                    data={preferenceSubmissionChartData}
                                    labelKey="dashboard.preferenceSubmissionsOverTime"
                                />
                            )}
                        </div>

                        {gapBlock}
                        {trendBlock}
                        {preferencesBlock}
                    </>
                )}

                {/* ---------- PHASE 2: Mentor Selection Period ---------- */}
                {phase === "mentor-selection" && (
                    <>
                        {statCardsBlock}

                        {/* Unmatched diagnosis (left) + mentees choosing mentor chart (right) */}
                        <div className="p-6 pt-0 grid gap-4 md:grid-cols-2">
                            {unmatchedDiagnosisCard}
                            {selectionChartData ? (
                                <InlineLineChart
                                    data={selectionChartData}
                                    labelKey="dashboard.mentorSelectionsOverTime"
                                />
                            ) : (
                                <Card>
                                    <CardHeader>
                                        <CardDescription className="text-base">
                                            {t("dashboard.mentorSelectionsOverTime")}
                                        </CardDescription>
                                    </CardHeader>
                                    <div className="px-6 pb-6 text-sm text-muted-foreground">
                                        {t("common.loading")}
                                    </div>
                                </Card>
                            )}
                        </div>

                        {mentorshipGroupsBlock}
                        {gapBlock}
                        {trendBlock}
                        {preferencesBlock}
                    </>
                )}

                {/* ---------- PHASE 3: After Mentor Selection ---------- */}
                {phase === "after-selection" && (
                    <>
                        {statCardsBlock}
                        {calendarBlock}
                        {mentorshipGroupsBlock}
                        {gapBlock}
                        {trendBlock}

                        {/* Unmatched diagnosis + activity timeline side-by-side */}
                        <div className="p-6 pt-0 grid gap-4 md:grid-cols-2">
                            {unmatchedDiagnosisCard}
                            {activityTimelineCard}
                        </div>

                        {preferencesBlock}

                        {/* Three line charts side-by-side */}
                        <SectionTitle className="p-6 pb-0">{t("dashboard.seasonTrends")}</SectionTitle>
                        <div className="p-6 pt-4 grid gap-4 md:grid-cols-3">
                            {profileCompletionChartData && (
                                <InlineLineChart
                                    data={profileCompletionChartData}
                                    labelKey="dashboard.profileCompletionsOverTime"
                                />
                            )}
                            {preferenceSubmissionChartData && (
                                <InlineLineChart
                                    data={preferenceSubmissionChartData}
                                    labelKey="dashboard.preferenceSubmissionsOverTime"
                                />
                            )}
                            {selectionChartData && (
                                <InlineLineChart
                                    data={selectionChartData}
                                    labelKey="dashboard.mentorSelectionsOverTime"
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default AdminDashboardPage;
